import './OrganizationEdit.scss';

import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react'
import { Breadcrumb, Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Organizations } from '../../api/Organizations';
import AppLoader from '../../components/AppLoader';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { Organization } from '../../models/Organization';
import { cloneDeep, set } from 'lodash';
import IconButton from '../../components/IconButton';
import { useAuth } from '../../contexts/auth';

export default function OrganizationEdit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [organization, setOrganization] = useState<Organization>();

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();

  const [validated, setValidated] = useState<boolean>();
  const [logoUrl, setLogoUrl] = useState<string | null>();
  const [logoImage, setLogoImage] = useState<File>();

  const { token } = useAuth();

  useEffect(() => {
    if (id) initOrganization();
    else setOrganization(new Organization());
    // eslint-disable-next-line
  }, []);

  const initOrganization = () => {
    Organizations.getOrganization(id!)
      .then(
        ({ data: organization }) => {
          setOrganization(organization)
          setLogoUrl(`${window.__env.BASE_URL}/organizations/${id}/logo?access_token=${token}`);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast!('organization-error', message, `Organizations Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }

  const cancel = () => navigate('/organizations');

  const handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.stopPropagation();
    evt.preventDefault();
    setValidated(true);

    if (logoImage && logoImage?.type !== 'image/svg+xml') {
      showToast!('organization-error', 'The image format is not SVG, please retry with an svg logo', `Logo upload Error`, 'danger');
      return
    }

    if (form.checkValidity() === true) {
      showLoader!();

      let org = organization!
      org.privacyMode = Boolean(org.privacyMode)

      const promise = id
        ? Organizations.updateOrganization(org as Organization)
        : Organizations.createOrganization(org as Organization);

      promise.then(
        ({ data: newOrganization }) => {
          if (!logoImage) {
            hideLoader!();
            showToast!('organization-success', `Organization ${id ? 'updated' : 'created'} successfully`, 'Success', 'success');
            navigate('/organizations');
            return;
          }

          const newId = id ? Number(id) : newOrganization.id!;
          const formData = new FormData();
          formData.append("logo", logoImage!);

          Organizations.uploadOrganizationLogo(newId, formData)
            .then(
              () => {
                hideLoader!();
                showToast!('organization-success', `Organization ${newId ? 'updated' : 'created'} successfully`, 'Success', 'success');
                navigate('/organizations');
                window.location.reload()
              },
              (err) => {
                const message = err.response?.data?.message || err.message;
                const status = err.response?.status;
                hideLoader!();
                showToast!('organization-error', message, `Organization Error ${status ? `(${status})` : ''}`, 'danger');
              }
            );
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('organization-error', message, `Organization Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
    }
  }

  const handleFieldChange = (path: string) => (evt: React.ChangeEvent<any>) => {
    const value = evt.target.value;
    const newOrganization = cloneDeep(organization) as Organization;
    set(newOrganization, path, value);
    setOrganization(newOrganization);
  }

  const handleFileSelect = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/svg, .svg';
    input.onchange = (evt) => {
      const files = Array.from((evt.target as HTMLInputElement).files as FileList);
      
      if (files[0].type !== 'image/svg+xml') {
        showToast!('organization-error', 'The image format is not SVG, please retry with an svg logo', `Logo upload Error`, 'danger');
        return
      }
      const url = URL.createObjectURL(files[0]);
      setLogoImage(files[0]);
      setLogoUrl(url);
      // URL.revokeObjectURL(url);
    };
    input.click();
  }

  const handleFileDrop = (evt: React.DragEvent<HTMLElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    evt.currentTarget.classList.remove('highlight');
    const files = Array.from((evt.dataTransfer.files as FileList));

    if (files[0].type !== 'image/svg+xml') {
      showToast!('organization-error', 'The image format is not SVG, please retry with an svg logo', `Logo upload Error`, 'danger');
      return
    }

    const url = URL.createObjectURL(files[0]);
    setLogoImage(files[0]);
    setLogoUrl(url);
  }

  const removeImage = (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setLogoUrl(null);
  }

  const toggleHighlight = (highlight: boolean) => (evt: React.DragEvent<HTMLElement>) => {
    evt.preventDefault();
    const element = evt.currentTarget;
    highlight ? element.classList.add('highlight') : element.classList.remove('highlight')
  }

  const renderOrganizationForm = () => {
    return (
      <>
        <div className="border-bottom py-2 px-3">
          <Card.Title className="mb-0">{id ? organization?.name : t('organizationEdit.title')}</Card.Title>
        </div>

        <Form validated={validated} noValidate onSubmit={handleSubmit}>
          <Card.Body>
            <Row>
              <Col md={4}>
                <Card className="file-upload" onClick={handleFileSelect} onDragOver={toggleHighlight(true)} onDragLeave={toggleHighlight(false)} onDrop={handleFileDrop}>
                  <Card.Body>
                    <div className="text-center py-4">
                      {logoUrl
                        ? (
                          <div className="image-preview">
                            <IconButton className="remove-button" onClick={removeImage}>
                              <FontAwesomeIcon size="2x" icon={solid('times')} />
                            </IconButton>
                            <img id="logo-preview" src={logoUrl} alt="Logo Preview" />
                          </div>
                        )
                        : (
                          <>
                            <FontAwesomeIcon size="3x" icon={solid('image')} />
                            <h4 className="mb-0 mt-3">Drop an SVG</h4>
                            <h6 className="my-2">or</h6>
                            <h4 className="mb-0">Click to Choose an SVG</h4>
                          </>
                        )
                      }
                    </div>
                  </Card.Body>
                  <Card.Footer>
                    <h6 className="text-center mb-0">{t('organizationEdit.logo')}</h6>
                  </Card.Footer>
                </Card>
              </Col>

              <Col>
                <Form.Group controlId="organization-name" className="mb-3">
                  <Form.Label>{t('common.name')}</Form.Label>
                  <Form.Control placeholder={t('organizationEdit.organizationName')} size="sm" value={organization!.name} onChange={handleFieldChange('name')} required />
                  <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('common.name') })}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="organization-description" className="mb-3">
                  <Form.Label>{t('common.description')}</Form.Label>
                  <Form.Control as="textarea" rows={3} placeholder={t('organizationEdit.organizationDescription')} size="sm" value={organization!.description} onChange={handleFieldChange('description')} />
                </Form.Group>

                <Form.Group controlId="organization-tenant" className="mb-3">
                  <Form.Label>{t('common.tenant')}</Form.Label>
                  <Form.Control placeholder={t('organizationEdit.organizationTenant')} size="sm" value={organization!.tenantId} onChange={handleFieldChange('tenantId')} required />
                  <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('common.tenant') })}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="organization-address" className="mb-3">
                  <Form.Label>{t('common.address')}</Form.Label>
                  <Form.Control placeholder={t('organizationEdit.organizationAddress')} size="sm" value={organization!.address} onChange={handleFieldChange('address')} required />
                  <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('common.address') })}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            {!id && (
              <>
                <div className="d-flex align-items-center mt-3 mb-2">
                  <FontAwesomeIcon size="sm" icon={solid('user')} />
                  <h6 className="ms-2 mb-0">{t('common.admin')}</h6>
                </div>

                <Row className="gx-2">
                  <Col md={6}>
                    <Form.Group controlId="user-firstname" className="mb-3">
                      <Form.Label>{t('userEdit.firstname')}</Form.Label>
                      <Form.Control size="sm" placeholder={t('userEdit.placeholders.firstname')} value={organization!.admin.firstname} onChange={handleFieldChange('admin.firstname')} required />
                      <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('userEdit.firstname') })}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="user-lastname" className="mb-3">
                      <Form.Label>{t('userEdit.lastname')}</Form.Label>
                      <Form.Control size="sm" placeholder={t('userEdit.placeholders.lastname')} value={organization!.admin.lastname} onChange={handleFieldChange('admin.lastname')} required />
                      <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('userEdit.lastname') })}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="user-email" className="mb-3">
                      <Form.Label>Email</Form.Label>
                      <Form.Control size="sm" placeholder={t('userEdit.placeholders.email')} value={organization!.admin.email} onChange={handleFieldChange('admin.email')} type="email" required />
                      <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: "Email" })}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="gx-2">
                  <Col md={6}>
                    <Form.Group controlId="user-username" className="mb-3">
                      <Form.Label>Username</Form.Label>
                      <Form.Control placeholder={t('userEdit.placeholders.username')} size="sm" value={organization!.admin.username} onChange={handleFieldChange('admin.username')} required />
                      <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: "Username" })}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="user-password" className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <Form.Control placeholder={t('userEdit.placeholders.password')} size="sm" value={organization!.admin.password} onChange={handleFieldChange('admin.password')} type="password" minLength={8} required />
                      <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: "Password" })}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
          </Card.Body>

          <div className="d-flex justify-content-between border-top py-2 px-3">
            <Button variant="light" className="border" size="sm" onClick={cancel}>{t('common.cancel')}</Button>
            <Button variant="success" size="sm" type="submit">{t('common.confirm')}</Button>
          </div>
        </Form>
      </>
    )
  }

  const renderAsyncContent = () => {
    if (id && !organization) return (
      <div className="d-flex justify-content-center py-3">
        <AppLoader size={150} />
      </div>
    )

    return organization && renderOrganizationForm();
  }

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/organizations' }}>{t('pages.organizations')}</Breadcrumb.Item>
        <Breadcrumb.Item active>{id ? organization?.name : t('userEdit.currentPage')}</Breadcrumb.Item>
      </Breadcrumb>

      <Card>{renderAsyncContent()}</Card>
    </Container>
  )
}